import React from 'react';
import './App.css'

import {
   Admin,
   Resource,
   CustomRoutes
} from 'react-admin'

import {
   ActionsLogsList,
   DefaultGoogleCampaignSettingsList,
   ConversionsReportsList,
   ReportSettingsList,
   ReportSponsoredSettingsList,
   TopBrandsSettingsList,
   UserSettingsList,
   PlatformAccountSettingsList,
   CampaignSettingsList,
   MediaPlatformSettingsList,
   GoogleAdsBridgesList,
   AdsActionsList,
   AdsIngestRunsList,
   AdsJobsList,
   AdsWriteSettingsList,
   CronSettingsList,
   RedirectDomainsList,
   RedirectLinksList,
   AffiliateSampleRequestsList,
   AffiliateAccessList,
   AdvertiserAccessList,
   MetaApiDataList,
   AggregatedMetaApiDataList,
} from './components/react-admin/lists'

import {
   DefaultGoogleCampaignSettingsEdit,
   ConversionsReportsEdit,
   ReportSettingsEdit,
   ReportSponsoredSettingsEdit,
   TopBrandsSettingsEdit,
   UserSettingsEdit,
   PlatformAccountSettingsEdit,
   MediaPlatformSettingsEdit,
   GoogleAdsBridgesEdit,
   AdsWriteSettingsEdit,
   AdsJobsEdit,
   CampaignSettingsEdit,
   CronSettingsEdit,
   ArticlesSetupEdit,
   AffiliateSampleRequestsEdit,
   AdvertiserAccessEdit,
} from './components/react-admin/edits'

import {
   DefaultGoogleCampaignSettingsCreate,
   ConversionsReportsCreate,
   ReportSettingsCreate,
   ReportSponsoredSettingsCreate,
   TopBrandsSettingsCreate,
   UserSettingsCreate,
   PlatformAccountSettingsCreate,
   MediaPlatformSettingsCreate,
   GoogleAdsBridgesCreate,
   AdsActionsCreate,
   CampaignSettingsCreate,
   CronSettingsCreate,
   RedirectDomainsCreate,
   AffiliateAccessCreate,
   AdvertiserAccessCreate,
} from './components/react-admin/creates'

import {
   DownloadConversionReportPage,
   VersionHistoryPage,
   UpdateTunePage,
   UploadReportPage,
} from './components/analytics-dashboard'

import { CustomLayout } from './layout/CustomLayout'
import { Route } from 'react-router-dom'
import { lightTheme, propticaLightTheme } from './layout/themes'
import { getSelectedIndustryId } from './utils/industryUtils'
import { authProvider, dataProvider } from './config'
import { ReportActionsPage } from './components/analytics-dashboard'
import { ActionsPage } from './components/analytics-dashboard/pages/ActionsPage'
import { NotAllowedPage } from './components/helperComponents/NotAllowedPage'
import { UserIDsBulkUpdatePage } from './components/helperComponents/UserIDsBulkUpdatePage'
import { AdsActionsShow } from './components/react-admin/settings/ads_actions/AdsActions.Show'
import { AdsIngestRunsShow } from './components/react-admin/settings/ads_ingest_runs/AdsIngestRuns.Show'
import { AdsJobsShow } from './components/react-admin/settings/ads_jobs/AdsJobs.Show'
import { ArticlesSetupList } from './components/react-admin/redirect-links/articles_setup/ArticlesSetup.List'
import {
   OpenRequestUnit
} from './components/react-admin/sample-system/affiliate-sample-requests/components/OpenRequestUnit'
import { USER_ROLES } from './config/constants/authConstants';
import { checkAccess } from './helpers/authUtils';

const App = () => {
   const industryClass = getSelectedIndustryId() === 2 ? 'industry-prop' : 'industry-hemp';

   return (
      <div className={`admin-wrapper ${industryClass}`}>
         <Admin
            authProvider={authProvider}
            dataProvider={dataProvider}
            dashboard={ActionsPage}
            layout={CustomLayout}
            theme={getSelectedIndustryId() === 2 ? propticaLightTheme : lightTheme}
         >
            {({role}) => {
               return [
                  <CustomRoutes>
                     <Route path="/not-allowed" element={<NotAllowedPage/>}/>
                     <Route path="/upload-report" element={<UploadReportPage/>}/>
                     <Route path="/report-actions" element={<ReportActionsPage/>}/>
                     <Route path="/download-report" element={<DownloadConversionReportPage/>}/>
                     <Route path="/sample_system/affiliate_sample_requests/edit/:row_id" element={<OpenRequestUnit/>}/>
                     {checkAccess({ role, allowedRoles: [USER_ROLES.ADMIN] })
                        ? <Route path="/provide-access-to-accounts" element={<UserIDsBulkUpdatePage/>}/>
                        : null
                     }
                     {checkAccess({
                        role,
                        allowedRoles: [
                           USER_ROLES.ADMIN,
                           USER_ROLES.MEDIA_BUYER,
                           USER_ROLES.CONTENT_MEDIA_MANAGER,
                           USER_ROLES.MEDIA_MANAGER
                        ]
                     })
                        ? <Route path="/update-tune" element={<UpdateTunePage/>}/>
                        : null
                     }
                     <Route path="/history" element={<VersionHistoryPage/>}/>
                  </CustomRoutes>,

                  checkAccess({ role, allowedRoles: [USER_ROLES.ADMIN] }) ?
                     <Resource
                        name="action_logs"
                        list={ActionsLogsList}
                     />
                     : null,

                  checkAccess({ role, allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.CONTENT_MEDIA_MANAGER] }) ?
                     <>
                        <Resource
                           name="articles_setup"
                           list={ArticlesSetupList}
                           edit={ArticlesSetupEdit}
                           create={ArticlesSetupEdit}
                        />
                        <Resource
                           name="redirect_domains"
                           list={RedirectDomainsList}
                           create={RedirectDomainsCreate}
                        />
                        <Resource
                           name="redirect_links"
                           list={RedirectLinksList}
                        />
                     </>
                     :
                     null,

                  checkAccess({
                     role,
                     allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.CONTENT_MEDIA_MANAGER, USER_ROLES.AFFILIATE_MANAGER]
                  }) ?
                     <>
                        <Resource
                           name="sample_system/affiliate_sample_requests"
                           list={AffiliateSampleRequestsList}
                           edit={AffiliateSampleRequestsEdit}
                        />
                        <Resource
                           name="sample_system/affiliate_access"
                           list={AffiliateAccessList}
                           create={AffiliateAccessCreate}
                        />
                        <Resource
                           name="sample_system/advertiser_access"
                           list={AdvertiserAccessList}
                           edit={AdvertiserAccessEdit}
                        />
                     </>
                     :
                     null,

                  checkAccess({ role, allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.MEDIA_BUYER] }) ?
                     <>
                        <Resource
                           name="campaign_settings"
                           list={CampaignSettingsList}
                           edit={CampaignSettingsEdit}
                           create={CampaignSettingsCreate}
                        />

                        <Resource
                           name="default_google_campaign_settings"
                           list={DefaultGoogleCampaignSettingsList}
                           edit={DefaultGoogleCampaignSettingsEdit}
                           create={DefaultGoogleCampaignSettingsCreate}
                        />

                        <Resource
                           name="conversion_reports"
                           list={ConversionsReportsList}
                           edit={ConversionsReportsEdit}
                           create={ConversionsReportsCreate}
                        />

                        <Resource
                           name="top_brand_settings"
                           list={TopBrandsSettingsList}
                           edit={TopBrandsSettingsEdit}
                           create={TopBrandsSettingsCreate}
                        />
                     </>
                     :
                     null,

                  checkAccess({
                     role,
                     allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.MEDIA_BUYER, USER_ROLES.MEDIA_MANAGER] }) ?
                     <>
                        <Resource
                           name="platform_account_settings"
                           list={PlatformAccountSettingsList}
                           edit={PlatformAccountSettingsEdit}
                           create={PlatformAccountSettingsCreate}
                        />

                        <Resource
                           name="report_settings"
                           list={ReportSettingsList}
                           edit={ReportSettingsEdit}
                           create={ReportSettingsCreate}
                        />

                        <Resource
                           name="report_sponsored_settings"
                           list={ReportSponsoredSettingsList}
                           edit={ReportSponsoredSettingsEdit}
                           create={ReportSponsoredSettingsCreate}
                        />
                     </>
                     : null,

                  checkAccess({ role, allowedRoles: [USER_ROLES.ADMIN] }) &&
                  <>
                     <Resource
                        name="user_settings"
                        list={UserSettingsList}
                        edit={UserSettingsEdit}
                        create={UserSettingsCreate}
                     />,

                     <Resource
                        name="cron_settings"
                        list={CronSettingsList}
                        edit={CronSettingsEdit}
                        create={CronSettingsCreate}
                     />,

                     <Resource
                        name="google_ads_bridges"
                        list={GoogleAdsBridgesList}
                        edit={GoogleAdsBridgesEdit}
                        create={GoogleAdsBridgesCreate}
                     />,

                     <Resource
                        name="ads_write_settings"
                        list={AdsWriteSettingsList}
                        edit={AdsWriteSettingsEdit}
                     />
                  </>,

                  checkAccess({ role, allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.MEDIA_MANAGER] }) &&
                  <>
                     <Resource
                        name="ads_actions"
                        list={AdsActionsList}
                        show={AdsActionsShow}
                        create={AdsActionsCreate}
                     />

                     <Resource
                        name="ads_ingest_runs"
                        list={AdsIngestRunsList}
                        show={AdsIngestRunsShow}
                     />

                     <Resource
                        name="ads_jobs"
                        list={AdsJobsList}
                        show={AdsJobsShow}
                        edit={checkAccess({ role, allowedRoles: [USER_ROLES.ADMIN] }) ? AdsJobsEdit : undefined}
                     />

                     <Resource
                        name="media_platform_api_settings"
                        list={MediaPlatformSettingsList}
                        edit={MediaPlatformSettingsEdit}
                        create={MediaPlatformSettingsCreate}
                     />

                     <Resource
                        name="meta_api_data"
                        list={MetaApiDataList}
                     />

                     <Resource
                        name="meta_api_data/aggregated-pixel"
                        list={AggregatedMetaApiDataList}
                     />
                  </>
               ]
            }}
         </Admin>
      </div>
   )
}

   export default App
